import React, { useContext } from 'react'
import {
  DepartmentNavigationTopLevelItemFragment,
  DepartmentNavigationSubSectionFragment,
  DepartmentNavigationTertiaryItemFragment
} from '../../NavHierarchyFragments.generated'

/**
 * We maintain a drill-down strategy for state management within the navigation. Since
 * you're not bound to the department you're contextually in, you can view all and
 * navigate to all sub-sections.
 *
 * As you hover and drill into sub-sections, you sticky the item that got you to that level.
 * The `activeNItems` and `activeNItem` pattern keeps track of the currently expanded items
 * in addition to the one that was the parent.
 */
export type NavigationContextType = {
  /**
   * Is the global veil visbile below the navigation
   */
  onToggleIsVeilVisible: (isVisible: boolean) => void
  isVeilVisible: boolean
  /**
   * Toggle if search is visible, this covers desktop and mobile
   */
  onToggleIsSearchVisible: (isVisible: boolean) => void
  isSearchVisible: boolean
  /**
   * Toggle if mobile browse menu is visible, this is mobile only
   */
  onToggleIsMobileBrowseMenuVisible: (isVisible: boolean) => void
  isMobileBrowseMenuVisible: boolean
  /**
   * Toggle if mobile account menu is visible, this is mobile only
   */
  onToggleIsMobileAccountMenuVisible: (isVisible: boolean) => void
  isMobileAccountMenuVisible: boolean
  /**
   * Since our menu and the veil is state controlled, we need some actionable
   * prop to both disable and menus and reset the drill down state.
   */
  onNavItemSelected: () => void
  /**
   * This is the just the current active department slug for any resolution, we use this to drill down
   * into the specific departments.
   */
  onSetActiveDepartmentSlug: (departmentSlug: string | undefined | null) => void
  activeDepartmentSlug: string | undefined | null
  /**
   * In some cases we further constrain the navigation by category slug (e.g. Winter Sports)
   */
  onSetCategorySlugConstraint: (categorySlug: string | null) => void
  categorySlugConstraint: string | null
  /**
   * This is the second level, commonly the topLevelCategory of the department, e.g. 'Snowboarding' or 'Skiing'. This
   * is for mobile only since the menu drills down.
   */
  onSetActiveCategory: (activeCategory: DepartmentNavigationTopLevelItemFragment | null) => void
  activeCategory: DepartmentNavigationTopLevelItemFragment | null
  /**
   * Sub sections are related to the parent attribute, but may not actually be a categoyr. We loosen the verbiage
   * here to be anything that is nested under the active category. e.g. 'Snowboard Accessories' or 'Deals'. This
   * is for mobile only since the menu drills down.
   */
  onSetActiveSubSections: (subSections: DepartmentNavigationSubSectionFragment[]) => void
  activeSubSections: DepartmentNavigationSubSectionFragment[]
  onSetActiveSubSection: (subSection: DepartmentNavigationSubSectionFragment | null) => void
  activeSubSection: DepartmentNavigationSubSectionFragment | null
  /**
   * Tertiary items are the third level and have the same lax constraints as the subSections. This is for mobile only
   * since the menu drills down.
   */
  onSetActiveTertiaryItems: (tertiaryItems: DepartmentNavigationTertiaryItemFragment[]) => void
  activeTertiaryItems: DepartmentNavigationTertiaryItemFragment[]
}

const NavigationContext = React.createContext<NavigationContextType | undefined>(undefined)

const NavigationContextProvider = NavigationContext.Provider
const NavigationContextConsumer = NavigationContext.Consumer

const useNavigationContext = () => {
  const navContext = useContext(NavigationContext)

  if (!NavigationContext) {
    throw new Error('Invoked `NavigationContext` outside of provider')
  }

  return navContext
}

export {
  useNavigationContext,
  NavigationContext,
  NavigationContextConsumer,
  NavigationContextProvider
}
